import api from "../../../../services/api";

const getSignature = () => localStorage.getItem("signature");

const post = async (path: string, body: Record<string, any>) => {
  const response = await api.post(path, {
    signature: getSignature(),
    ...body,
  });
  return response.data?.data;
};

export const listIssues = async (
  companyId: string,
  companyType: string,
  filters: Record<string, any>
) =>
  post("projectService/issue/listIssues", {
    company_id: companyId,
    company_type: companyType,
    ...filters,
  });

export const searchIssues = async (
  companyId: string,
  companyType: string,
  filters: Record<string, any>
) =>
  post("projectService/issue/searchIssues", {
    company_id: companyId,
    company_type: companyType,
    ...filters,
  });

export const getIssueById = async (
  companyId: string,
  companyType: string,
  issueId: number
) =>
  post("projectService/issue/getIssueById", {
    company_id: companyId,
    company_type: companyType,
    id: issueId,
  });

export const getIssueByKey = async (
  companyId: string,
  companyType: string,
  issueKey: string
) =>
  post("projectService/issue/getIssueById", {
    company_id: companyId,
    company_type: companyType,
    issue_key: issueKey,
  });

export const createIssue = async (
  companyId: string,
  companyType: string,
  payload: Record<string, any>
) =>
  post("projectService/issue/createIssue", {
    company_id: companyId,
    company_type: companyType,
    ...payload,
  });

export const updateIssue = async (
  companyId: string,
  companyType: string,
  issueId: number,
  payload: Record<string, any>
) =>
  post("projectService/issue/updateIssue", {
    company_id: companyId,
    company_type: companyType,
    id: issueId,
    ...payload,
  });

export const moveIssue = async (
  companyId: string,
  companyType: string,
  payload: {
    id: number;
    status_id?: number;
    sprint_id?: number | null;
    before_id?: number | null;
    after_id?: number | null;
  }
) =>
  post("projectService/issue/moveIssue", {
    company_id: companyId,
    company_type: companyType,
    ...payload,
  });

export const deleteIssue = async (
  companyId: string,
  companyType: string,
  issueId: number
) =>
  post("projectService/issue/deleteIssue", {
    company_id: companyId,
    company_type: companyType,
    id: issueId,
  });

export const setIssueLabels = async (
  companyId: string,
  companyType: string,
  issueId: number,
  labelIds: number[]
) =>
  post("projectService/issue/setIssueLabels", {
    company_id: companyId,
    company_type: companyType,
    issue_id: issueId,
    label_ids: labelIds,
  });

export const setIssueCustomFields = async (
  companyId: string,
  companyType: string,
  issueId: number,
  values: Array<{ field_id: number; value: any }>
) =>
  post("projectService/issue/setIssueCustomFields", {
    company_id: companyId,
    company_type: companyType,
    issue_id: issueId,
    values,
  });

export const listSubtasks = async (
  companyId: string,
  companyType: string,
  parentTaskId: number
) =>
  post("projectService/issue/listSubtasks", {
    company_id: companyId,
    company_type: companyType,
    parent_task_id: parentTaskId,
  });

export const listEpicChildren = async (
  companyId: string,
  companyType: string,
  epicId: number
) =>
  post("projectService/issue/listEpicChildren", {
    company_id: companyId,
    company_type: companyType,
    epic_id: epicId,
  });

export const listComments = async (
  companyId: string,
  companyType: string,
  issueId: number
) =>
  post("projectService/comment/listComments", {
    company_id: companyId,
    company_type: companyType,
    issue_id: issueId,
  });

export const createComment = async (
  companyId: string,
  companyType: string,
  payload: Record<string, any>
) =>
  post("projectService/comment/createComment", {
    company_id: companyId,
    company_type: companyType,
    ...payload,
  });

export const updateComment = async (
  companyId: string,
  companyType: string,
  commentId: number,
  body: string
) =>
  post("projectService/comment/updateComment", {
    company_id: companyId,
    company_type: companyType,
    id: commentId,
    body,
  });

export const deleteComment = async (
  companyId: string,
  companyType: string,
  commentId: number
) =>
  post("projectService/comment/deleteComment", {
    company_id: companyId,
    company_type: companyType,
    id: commentId,
  });

export const listAttachments = async (
  companyId: string,
  companyType: string,
  entityType: string,
  entityId: number
) =>
  post("projectService/attachment/listAttachments", {
    company_id: companyId,
    company_type: companyType,
    entity_type: entityType,
    entity_id: entityId,
  });

export const attachFile = async (
  companyId: string,
  companyType: string,
  payload: {
    entity_type: string;
    entity_id: number;
    file_name: string;
    url: string;
    public_id?: string | null;
    resource_type?: string | null;
    mime_type?: string | null;
    size_bytes?: number | null;
  }
) =>
  post("projectService/attachment/attachFile", {
    company_id: companyId,
    company_type: companyType,
    ...payload,
  });

export const deleteAttachment = async (
  companyId: string,
  companyType: string,
  attachmentId: number
) =>
  post("projectService/attachment/deleteAttachment", {
    company_id: companyId,
    company_type: companyType,
    id: attachmentId,
  });

export const bulkUpdateIssues = async (
  companyId: string,
  companyType: string,
  payload: { issue_ids: number[]; updates: Record<string, any> }
) =>
  post("projectService/issue/bulkUpdate", {
    company_id: companyId,
    company_type: companyType,
    ...payload,
  });

export const bulkDeleteIssues = async (
  companyId: string,
  companyType: string,
  issueIds: number[]
) =>
  post("projectService/issue/bulkDelete", {
    company_id: companyId,
    company_type: companyType,
    issue_ids: issueIds,
  });

export const bulkTransitionIssues = async (
  companyId: string,
  companyType: string,
  payload: { issue_ids: number[]; status_id: number; resolution_id?: number | null }
) =>
  post("projectService/issue/bulkTransition", {
    company_id: companyId,
    company_type: companyType,
    ...payload,
  });
